// Human-in-the-loop gate for destructive/supervised tool calls. The agent's
// workflow is parked on a Temporal signal while this card is undecided;
// Approve/Reject files the decision (POST /approvals/{id}/decide) which
// resumes it. If nobody decides within the server's approval window the
// request auto-rejects, so an abandoned card can't strand the turn.

import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Text } from '@kula-ai/components';
import { decideApproval } from '../../../api/approvals';
import type { ApprovalBlockData } from '../../../types';

interface ApprovalBlockProps {
  data: ApprovalBlockData;
}

// "kula_contact_delete" → "contact delete" — readable without a label table.
const humanizeTool = (tool: string): string =>
  tool.replace(/^kula_/, '').replaceAll('_', ' ');

type DecisionState =
  | 'pending'
  | 'deciding'
  | 'approved'
  | 'rejected'
  | 'failed';

export const ApprovalBlock = ({ data }: ApprovalBlockProps) => {
  const [state, setState] = useState<DecisionState>('pending');

  const decide = (approved: boolean) => {
    if (state === 'deciding') return;
    setState('deciding');
    decideApproval(data.recordId, approved)
      .then(() => setState(approved ? 'approved' : 'rejected'))
      .catch(() => setState('failed'));
  };

  const decided = state === 'approved' || state === 'rejected';

  return (
    <Box
      data-test-id="agentforge-approval-block"
      border="1px solid"
      borderColor={decided ? 'secondary-200' : 'warning-300'}
      borderRadius="12px"
      bg={decided ? 'secondary-25' : 'warning-25'}
      px="16px"
      py="12px"
      maxW="520px"
    >
      <Text variant="span" className="text-secondary-900 font-semibold">
        Approval needed: {humanizeTool(data.toolName)}
      </Text>
      {data.reason ? (
        <Text variant="small" as="p" className="text-secondary-600 mt-1">
          {data.reason}
        </Text>
      ) : null}
      <Text variant="small" as="p" className="text-secondary-500 mt-1">
        This action can’t be quietly undone, so it always needs a human
        sign-off. Nothing runs until you decide.
      </Text>

      <Flex mt="10px" gap="8px" align="center">
        {state === 'pending' || state === 'deciding' ? (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => decide(true)}
              isDisabled={state === 'deciding'}
              data-test-id="agentforge-approval-approve"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => decide(false)}
              isDisabled={state === 'deciding'}
              data-test-id="agentforge-approval-reject"
            >
              Reject
            </Button>
          </>
        ) : state === 'failed' ? (
          <Text variant="small" className="text-error-600">
            Couldn’t record the decision — try again.
          </Text>
        ) : (
          <Text variant="small" className="text-secondary-600 font-medium">
            {state === 'approved'
              ? '✓ Approved — running now.'
              : '✕ Rejected — the agent will skip it.'}
          </Text>
        )}
        {state === 'failed' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setState('pending')}
          >
            Retry
          </Button>
        ) : null}
      </Flex>
    </Box>
  );
};
